import { BackButtonBar } from '@/components/Mobile/BackButtonBar';
import { getDimensions } from '@/utils';
import clsx from 'clsx';
import { Children, PropsWithChildren } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

interface IProps {
  title?: string;
  description?: string;
  theme?: 'light' | 'dark';
  /**
   * to remove the back button bar
   */
  hideBack?: boolean;
  className?: string;
  titleClassName?: string;
  backBarClassName?: string;
  backBarStyles?: React.CSSProperties;
  /**
   * props for the back button bar
   */
  backBarProps?: Partial<React.ComponentProps<typeof BackButtonBar>>;
  childrenContainerClassName?: string;
  childrenContainerStyles?: React.CSSProperties;
  /**
   * callback for back button click.
   * defaults to navigate(-1) from react-router
   */
  onBack?: () => void;
  rightAction?: React.ReactNode;
  /**
   * to make the header take the full width (1120px)
   */
  fullWidth?: boolean;
  /**
   * to center the main container and have a max-width of 800px
   */
  centered?: boolean;
  renderOrder?: Array<'title' | 'description' | 'children'>;
  bg?: string;
  renderTitleInBackBar?: boolean;
  forceRenderTitleInBackBar?: boolean;
}

const PageHeader = ({
  title,
  description,
  hideBack,
  children,
  theme,
  rightAction,
  onBack,
  fullWidth,
  centered,
  renderOrder = ['children', 'title', 'description'],
  className,
  titleClassName,
  backBarClassName,
  backBarStyles,
  childrenContainerClassName,
  childrenContainerStyles,
  bg,
  renderTitleInBackBar,
  forceRenderTitleInBackBar,
  backBarProps,
}: PropsWithChildren<IProps>) => {
  const navigate = useNavigate();
  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
    } else if (typeof window?.NativeBridge?.onNativeClose === 'function') {
      window.NativeBridge.onNativeClose();
    } else {
      navigate(-1);
    }
  };

  const backBarHeight = getDimensions('claims-back-bar')?.height ?? 0;
  const navbarHeight = getDimensions('claims-navbar')?.height ?? 0;

  const { ref: headingRef, inView: isHeaderInView } = useInView({
    skip: !renderTitleInBackBar,
    rootMargin: `-${backBarHeight + navbarHeight}px`,
  });

  const [searchParams] = useSearchParams();
  const hideBackParam = searchParams.get('hb') === 'true';

  const renderer = {
    children,
    title: title && !forceRenderTitleInBackBar && (
      <h1
        ref={headingRef}
        className={clsx({
          'text-3xl lg:text-[32px] font-black font-chromatica text-gray-600':
            true,
          'text-white': theme === 'dark',
          'text-gray-600': theme === 'light',
        })}
      >
        {title}
      </h1>
    ),
    description: description && (
      <h2
        className={clsx({
          'text-sm lg:text-lg text-gray-500': true,
          'text-white': theme === 'dark',
          'text-gray-500': theme === 'light',
        })}
      >
        {description}
      </h2>
    ),
  };

  return (
    <div
      id="claims-page-header"
      className={twMerge(
        clsx({
          'flex flex-col lg:flex-row lg:items-center gap-y-3 gap-x-14 mx-auto w-full max-w-[1120px]':
            true,
          [`${bg}`]: bg,
          '!gap-x-0': centered,
        }),
        className
      )}
    >
      {hideBack || hideBackParam ? null : (
        <BackButtonBar
          title={
            (renderTitleInBackBar && !isHeaderInView) ||
            forceRenderTitleInBackBar
              ? title
              : null
          }
          bg={bg}
          className={backBarClassName}
          onBack={handleBack}
          theme={theme}
          style={backBarStyles}
          {...backBarProps}
        />
      )}
      {(title || description || children || rightAction) && (
        <div
          className={twMerge(
            clsx({
              'w-full flex flex-col md:flex-row md:items-center gap-y-6 gap-x-14 flex-1 px-5 lg:px-0':
                true,
              'max-w-[1120px]': fullWidth,
              'max-w-[928px] mx-auto': !fullWidth,
              'ml-0': !fullWidth && !(hideBack || hideBackParam),
              'ml-auto mr-auto md:max-w-[840px] lg:max-w-[800px] lg:relative -left-5':
                centered,
            }),
            titleClassName
          )}
        >
          {(title || description || children) && (
            <div
              className={twMerge(
                clsx({
                  'flex flex-col text-left gap-y-2 flex-1': true,
                  'text-white': theme === 'dark',
                  'text-retail-text-main': theme === 'light',
                }),
                childrenContainerClassName
              )}
              style={childrenContainerStyles}
            >
              {Children.map(renderOrder, (elem) => renderer[elem])}
            </div>
          )}
          {rightAction}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
